import './ProjectEdit.scss';

import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useMemo } from 'react'
import { useEffect } from 'react';
import { useState } from 'react';
import { Alert, Card, Container, Row, Col, Breadcrumb, Badge, Modal, Button, Form } from 'react-bootstrap'
import { Helmet } from 'react-helmet'
import { Link, useParams } from 'react-router-dom'
import { Projects } from '../../api/Projects';
import AlidaLoader from '../../components/common/AlidaLoader';
import { useToasts } from '../../contexts/toasts';
import { Flex, Pagination, Select, Tabs, Tooltip } from 'antd';
import { Datasets } from '../../api/Datasets';
import { Models } from '../../api/Models';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import AppSearch from '../../components/common/AppSearch';
import { ProjectAsset } from '../../components/list-items/ProjectAsset';
import AssetItem from '../../components/list-items/AssetItem';
import { memo } from 'react';
import { debounce } from 'lodash';
import { accessLevelOptions } from '../../utils';
import { Project } from '../../models/Project';
import { BdaDataset } from '../../models/BdaDataset';
import { BdaModel } from '../../models/BdaModel';
import { PagedResults } from '../../models/generics';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';
import AppLoader from '../../components/common/AppLoader';
import AppTag from '../../components/common/AppTag';

export default function ProjectEdit() {
  const { id } = useParams();
  const [project, setProject] = useState<Project>();
  const [newProject, setNewProject] = useState<Project>();

  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState(false);

  const [currentDatasetsPage, setCurrentDatasetsPage] = useState(1);
  const [currentModelsPage, setCurrentModelsPage] = useState(1);
  const [datasetSearch, setDatasetSearch] = useState("");
  const [modelSearch, setModelSearch] = useState("");

  const [datasets, setDatasets] = useState<BdaDataset[]>();
  const [models, setModels] = useState<BdaModel[]>();
  const [totalDatasets, setTotalDatasets] = useState<number>();
  const [totalModels, setTotalModels] = useState<number>();

  const [assetToRemove, setAssetToRemove] = useState<any>();
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const datasetLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasets"));
  const modelLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "models"));

  const addModelButtonDisabled = useMemo(() => (project?.models || []).length > 0, [project]);
  const addDatasetButtonDisabled = useMemo(() => (project?.datasets || []).length > 0, [project]);

  useEffect(() => {
    getProject();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (project) {
      setDatasets(undefined);
      getDatasets();
    }
    // eslint-disable-next-line
  }, [project, currentDatasetsPage, datasetSearch]);

  useEffect(() => {
    if (project) {
      setModels(undefined);
      getModels();
    }
    // eslint-disable-next-line
  }, [project, currentModelsPage, modelSearch]);

  const debounceDatasetSearch = useMemo(() => debounce(value => setDatasetSearch(value), 500), []);
  const debounceModelSearch = useMemo(() => debounce(value => setModelSearch(value), 500), []);

  useEffect(() => {
    return () => {
      debounceDatasetSearch.cancel();
      debounceModelSearch.cancel();
    };
  }, [debounceDatasetSearch, debounceModelSearch])

  const getProject = useCallback((refresh?: boolean) => {
    Projects.getProject(+id!)
      .then(
        ({ data: proj }) => {
          if (refresh) {
            setProject(p => ({
              ...p!,
              bdaApplications: proj.bdaApplications,
              datasets: proj.datasets,
              models: proj.models,
              services: proj.services,
            }));
            setLoading(false);
          }
          else {
            setProject(proj);
            setNewProject(proj);
          }
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('projects-error', message, `Projects Error ${status ? `(${status})` : ''}`, 'danger');
          setError(true);
        }
      )
  }, [id, showToast]);

  const refreshAssets = useCallback(() => {
    setLoading(true);
    getProject(true);
  }, [getProject]);

  const getDatasets = useCallback(() => {
    const params = new URLSearchParams();
    // params.set("evaluation", false);
    params.set("page", `${currentDatasetsPage}`);
    params.set("size", `${20}`);
    params.set("sort", "id");
    params.set("desc", `${true}`);
    if (datasetSearch.length) params.set("freeText", datasetSearch);
    if (project!.datasets?.length) params.set("excludeIds", project!.datasets.map(d => d.id).join(","));

    Datasets.getDatasets(params)
      .then(
        ({ data: datasetsPages }) => {
          setDatasets((datasetsPages as PagedResults<BdaDataset>).content);
          setTotalDatasets((datasetsPages as PagedResults<BdaDataset>).pageMetadata.totalElements);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasets-error', message, `${datasetLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
          setDatasets([]);
          // setError(true);
        }
      );
  }, [currentDatasetsPage, datasetSearch, datasetLabels, project, showToast]);

  const getModels = useCallback(() => {
    const params = new URLSearchParams();
    // params.set("evaluation", false);
    params.set("page", `${currentModelsPage}`);
    params.set("size", `${20}`);
    params.set("sort", "id");
    params.set("desc", `${true}`);
    if (modelSearch.length) params.set("freeText", modelSearch);
    if (project!.models?.length) params.set("excludeIds", project!.models.map(m => m.id).join(","));

    Models.getModels(params)
      .then(
        ({ data: modelsPages }) => {
          setModels((modelsPages as PagedResults<BdaModel>).content);
          setTotalModels((modelsPages as PagedResults<BdaModel>).pageMetadata.totalElements);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('models-error', message, `${modelLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
          setModels([]);
          // setError(true);
        }
      );
  }, [currentModelsPage, modelSearch, project, modelLabels, showToast]);

  const handleRemoveModalClose = useCallback(() => {
    setShowRemoveModal(false);
    setAssetToRemove(null);
  }, []);

  const handleAssetToRemove = useCallback((asset: any, type: string) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setAssetToRemove({ id: asset.id, name: asset.name, type });
    setShowRemoveModal(true);
  }, []);

  const handleEditProject = useCallback(() => setShowEditModal(true), []);
  const handleEditModalClose = useCallback(() => {
    setShowEditModal(false);
    setValidated(false);
  }, []);

  const handleFieldChange = useCallback((field: keyof Project) => (evt: any) => {
    const value = field === "accessLevel" || field === "tags" ? evt : evt.target.value;
    setNewProject(p => ({
      ...p!,
      [field]: value,
    }));
  }, []);

  const handleProjectSubmit = useCallback((evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.stopPropagation();
    evt.preventDefault();
    setValidated(true);

    if (form.checkValidity() === true) {
      showLoader();

      const body = { ...newProject };
      delete body.datasets;
      delete body.models;

      Projects.updateProject(body as Project)
        .then(
          ({ data }) => {
            hideLoader();
            showToast("projects-edit-success", "Project edited successfully", 'Project Success', 'success');
            setProject(proj => ({ ...proj!, name: data.name, description: data.description, accessLevel: data.accessLevel, tags: data.tags }));
            handleEditModalClose();
          },
          (err) => {
            hideLoader();
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            showToast(`projects-edit-error`, message, `Project Error ${status ? `(${status})` : ''}`, 'danger');
          }
        )
    }
  }, [showLoader, hideLoader, newProject, showToast, handleEditModalClose]);

  const addAsset = useCallback((asset: any, type: string) => () => {
    showLoader();

    const body = { id: asset.id, type };

    Projects.addAsset(+id!, body)
      .then(
        () => {
          hideLoader();
          showToast('asset-add-success', `Project updated, new ${type} added.`, "Project Success", 'success');
          refreshAssets();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('asset-add-error', message, `Project Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, showLoader, hideLoader, showToast, refreshAssets]);

  const removeAsset = useCallback(() => {
    showLoader();

    Projects.removeAsset(+id!, assetToRemove)
      .then(
        () => {
          hideLoader();
          showToast('asset-remove-success', `Project updated, ${assetToRemove.type} removed`, "Project Success", 'success');
          refreshAssets();
          handleRemoveModalClose();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('asset-remove-error', message, `Project Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showLoader, id, assetToRemove, hideLoader, showToast, refreshAssets, handleRemoveModalClose]);

  const changePage = useCallback((asset: 'applications' | 'datasets' | 'models' | 'services') => (page: number) => {
    if (asset === "datasets") setCurrentDatasetsPage(page);
    else setCurrentModelsPage(page);
  }, []);

  const renderAssets = useCallback(() => {
    return (
      <Card className="assets-card">
        <Card.Body className="p-0">
          <Tabs
            centered
            defaultActiveKey="applications"
            items={[
              {
                key: "datasets",
                label: (
                  <span>
                    <FontAwesomeIcon icon={solid('database')} />
                    <span className="ms-2">{datasetLabels.plural || "Datasets"}</span>
                  </span>
                ),
                children: (
                  <AssetTab
                    data={datasets as BdaDataset[]}
                    total={totalDatasets}
                    currentPage={currentDatasetsPage}
                    assetType="dataset"
                    assetLabel={datasetLabels.singular}
                    onPageChange={changePage("datasets")}
                    onSearchChange={debounceDatasetSearch}
                    onAdd={addAsset}
                    buttonDisabled={addDatasetButtonDisabled}
                  />
                )
              },
              {
                key: "models",
                label: (
                  <span>
                    <FontAwesomeIcon icon={solid('graduation-cap')} />
                    <span className="ms-2">{modelLabels.plural || "Models"}</span>
                  </span>
                ),
                children: (
                  <AssetTab
                    data={models as BdaModel[]}
                    total={totalModels}
                    currentPage={currentModelsPage}
                    assetType="model"
                    assetLabel={modelLabels.singular}
                    onPageChange={changePage("models")}
                    onSearchChange={debounceModelSearch}
                    onAdd={addAsset}
                    buttonDisabled={addModelButtonDisabled}
                  />
                )
              },
            ]}
          />
        </Card.Body>
      </Card>
    )
  }, [datasetLabels, modelLabels, addAsset, datasets, models, changePage,
    currentDatasetsPage, currentModelsPage, debounceDatasetSearch, debounceModelSearch,
    totalDatasets, totalModels, addModelButtonDisabled, addDatasetButtonDisabled
  ]);

  const renderProjectAssets = useCallback(() => {
    if (loading) return <AppLoader className="py-5" justify="center" size={60} />;

    if (
      project!.datasets?.length === 0 &&
      project!.models?.length === 0
    )
      return (
        <Alert className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">Project is empty. You can add new components from the <b>lists on the right</b>.</span>
        </Alert>
      );


    return (
      <>
        {project!.datasets.map(dataset => (
          <Col md={6} key={dataset.id} className="mb-3">
            <ProjectAsset
              asset={dataset}
              type="dataset"
              actions={
                <Tooltip title="Remove">
                  <button className="circle-button" onClick={handleAssetToRemove(dataset, "dataset")}>
                    <FontAwesomeIcon icon={solid('times')} fixedWidth />
                  </button>
                </Tooltip>
              }
            />
          </Col>
        ))}
        {project!.models.map(model => (
          <Col md={6} key={model.id} className="mb-3">
            <ProjectAsset
              asset={model}
              type="model"
              actions={
                <Tooltip title="Remove">
                  <button className="circle-button" onClick={handleAssetToRemove(model, "model")}>
                    <FontAwesomeIcon icon={solid('times')} fixedWidth />
                  </button>
                </Tooltip>
              }
            />
          </Col>
        ))}
      </>
    )
  }, [loading, project, handleAssetToRemove]);

  const renderProject = useCallback(() => {
    const privacyIcon = project!.accessLevel === "PRIVATE" ? solid('user-secret') : project!.accessLevel === "TEAM" ? solid('users') : solid('globe-africa')

    return (
      <Card>
        <Card.Header className="p-2 d-flex">
          <div className="p-3 rounded bg-project">
            <div className="position-relative text-white">
              <FontAwesomeIcon icon={solid("folder")} className="align-self-center" size="2x" />
            </div>
          </div>
          <Col className="px-3 align-self-center">
            <h4 className="mb-0">{project!.name}</h4>
            <p className="mb-0">{project!.description}</p>
          </Col>
          <Col md={2} className="mt-2">
            <Row className="justify-content-end me-1">
              <Badge className="w-auto shadow">
                <FontAwesomeIcon icon={privacyIcon} />
                <span className="ms-2">{project!.accessLevel}</span>
              </Badge>
            </Row>
            <Flex className="tags-wrapper mt-2">
              {project!.tags?.map(tag => <AppTag tag={tag} key={tag} />)}
            </Flex>
          </Col>
          <Col md={1} className="border-start ms-3 text-center align-self-center">
            <Tooltip title="Edit">
              <button className="circle-button dark" onClick={handleEditProject}>
                <FontAwesomeIcon icon={solid('pen')} />
              </button>
            </Tooltip>
          </Col>
        </Card.Header>
        <Card.Body>
          <Row className="g-0">{renderProjectAssets()}</Row>
        </Card.Body>
      </Card>
    )
  }, [project, renderProjectAssets, handleEditProject]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert className="py-2 m-0" variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    )

    if (!project) return (
      <Flex justify="center" className="py-5">
        <AlidaLoader size={300} />
      </Flex>
    )

    return (
      <>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/projects' }}>Projects</Breadcrumb.Item>
          <Breadcrumb.Item active>{project.name}</Breadcrumb.Item>
        </Breadcrumb>

        <Row className="gx-2">
          <Col className="mb-3 mb-md-0" md={6}>{renderProject()}</Col>
          <Col md={6}>{renderAssets()}</Col>
        </Row>
      </>
    );
  }, [error, project, renderProject, renderAssets]);

  return (
    <Container fluid className="project-edit my-2">
      <Helmet>
        <title>Project {id} Management</title>
      </Helmet>

      {renderAsyncContent()}

      <Modal show={showRemoveModal} onHide={handleRemoveModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Remove {assetToRemove?.type}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to remove {assetToRemove?.type} <b>{assetToRemove?.name}</b> from this group?</p>

          <Alert className="py-2 m-0">
            <FontAwesomeIcon icon={solid('info-circle')} />
            <span className="ms-2">The {assetToRemove?.type} will be again among the available ones.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleRemoveModalClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={removeAsset}>Remove</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={handleEditModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Project</Modal.Title>
        </Modal.Header>
        <Form noValidate validated={validated} onSubmit={handleProjectSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="project-name">
              <Form.Label>Name</Form.Label>
              <Form.Control size="sm" placeholder="Project Name" value={newProject?.name} onChange={handleFieldChange("name")} required />
              <Form.Control.Feedback type="invalid">Project Name is required.</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="asset-tags">
              <Form.Label>Tags</Form.Label>
              <Select mode="tags" className="w-100" placeholder="Project tags" value={newProject?.tags} onChange={handleFieldChange('tags')} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="project-description">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} size="sm" placeholder="Project Description" value={newProject?.description} onChange={handleFieldChange("description")} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="project-access-level">
              <Form.Label>Privacy Level</Form.Label>
              <Select
                allowClear
                placeholder="Select privacy level"
                value={newProject?.accessLevel}
                onChange={handleFieldChange('accessLevel')}
                className="w-100"
              >
                {accessLevelOptions
                  .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true)
                  .map((option, index) => (
                    <Select.Option key={index} value={option.value} label={option.label}>
                      <div>
                        <FontAwesomeIcon icon={option.icon} fixedWidth />
                        <span className="ms-2">{option.label}</span>
                      </div>
                    </Select.Option>
                  ))}
              </Select>
              {validated && !newProject?.accessLevel && <Form.Control.Feedback className="d-block" type="invalid">Project Privacy Level is required.</Form.Control.Feedback>}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" size="sm" className="border" onClick={handleEditModalClose}>Close</Button>
            <Button variant="success" size="sm" type="submit">Confirm</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  )
}

type TabProps = {
  data: BdaDataset[] | BdaModel[];
  total?: number;
  currentPage: number;
  assetType: 'dataset' | 'model';
  assetLabel?: string;
  onPageChange: (page: number, size: number) => void;
  onAdd: (item: any, type: string) => React.MouseEventHandler<HTMLButtonElement>;
  onSearchChange: (value: string) => void;
  buttonDisabled?: boolean;
}

const AssetTab = memo(({ data, total, currentPage, assetType, assetLabel = "Asset", onPageChange, onAdd, onSearchChange, buttonDisabled }: TabProps) => {

  const renderDataItem = useCallback((item: BdaDataset | BdaModel) => {
    return (
      <AssetItem
        asset={item}
        type={assetType}
        actions={
          <Tooltip title="Add">
            <button className="circle-button" onClick={onAdd(item, assetType)} disabled={buttonDisabled}>
              <FontAwesomeIcon icon={solid('plus')} fixedWidth />
            </button>
          </Tooltip>
        }
      />
    );
  }, [assetType, onAdd, buttonDisabled]);

  const renderData = useCallback(() => {
    if (!data) return <AppLoader className="py-5 my-5" justify="center" size={60} />;

    return data.length === 0
      ? (
        <Alert className="py-2 m-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No {assetType}s found.</span>
        </Alert>
      )
      : (
        <Row className="assets-wrapper p-2 mx-0 gx-3">
          {data.map(item => (
            <Col md={6} key={item.id} className="mb-3">{renderDataItem(item)}</Col>
          ))}
        </Row>
      )
  }, [data, renderDataItem, assetType]);

  return (
    <>
      <Flex justify="space-between" align="center" className="py-1 px-2 border-bottom">
        <Col className="pe-4">
          <AppSearch placeholder={`Search ${assetLabel}...`} onChange={onSearchChange} />
        </Col>

        <Pagination
          simple
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
          total={total}
          pageSize={20}
          current={currentPage}
          onChange={onPageChange}
          className="d-flex align-items-center"
        />
      </Flex>
      {renderData()}
    </>
  )
})